import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { CartProvider } from './lib/cart';
import { TableOrderProvider } from './lib/table-context';
import { KitchenPauseProvider } from './lib/kitchen-pause-context';
import { KitchenPausedBanner } from './components/restaurant/KitchenPausedBanner';
import { getSubdomainSlug } from './lib/subdomain';
// Customer Pages
import { HomePage } from './pages/HomePage';
import { RestaurantListingPage } from './pages/RestaurantListingPage';
import { RestaurantPage } from './pages/RestaurantPage';
import { MenuPage } from './pages/MenuPage';
import { CartPage } from './pages/CartPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { OrderConfirmationPage } from './pages/OrderConfirmationPage';
import { TrackingPage } from './pages/TrackingPage';
import { TablePage } from './pages/TablePage';
// Admin Pages
import { LoginPage } from './pages/admin/LoginPage';
import { NoizaMetricsPage } from './pages/admin/NoizaMetricsPage';
import { DashboardPage } from './pages/admin/DashboardPage';
import { KDSPage } from './pages/admin/KDSPage';
import { InventoryPage } from './pages/admin/InventoryPage';
import { MetricsPage } from './pages/admin/MetricsPage';
import { MenuManagementPage } from './pages/admin/MenuManagementPage';
import { ImportPage } from './pages/admin/ImportPage';
import { StationSelectPage } from './pages/admin/StationSelectPage';
import { CajaOverviewPage } from './pages/admin/CajaOverviewPage';
import { TableManagementPage } from './pages/admin/TableManagementPage';
import { TableQRPage } from './pages/admin/TableQRPage';
import { PublicMenuPage } from './pages/PublicMenuPage';
// Tenant Access
import { TenantProvider } from './lib/tenant-context';
// ─── MAINTENANCE GATE ───
// Slugs under maintenance — only accessible with ?dev=noiza2026
const MAINTENANCE_SLUGS = ['noiza.restaurant'];
function MaintenancePage() {
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const handleUnlock = () => {
    if (code === 'noiza2026') {
      sessionStorage.setItem('noiza_dev_access', 'true');
      window.location.reload();
    } else {
      setError(true);
    }
  };
  return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center p-6" data-id="element-0">
      <div className="max-w-md w-full text-center" data-id="element-1">
        <div className="w-16 h-16 mx-auto mb-6 bg-[#141414] rounded-2xl flex items-center justify-center border border-white/10" data-id="element-2">
          <span className="text-2xl" data-id="element-3">🔧</span>
        </div>
        <h1 className="text-2xl font-black text-white mb-2" data-id="element-4">
          En Mantenimiento
        </h1>
        <p className="text-white/40 text-sm mb-8" data-id="element-5">
          Este restaurante está temporalmente en modo de pruebas. Volveremos
          pronto.
        </p>
        <div className="space-y-3" data-id="element-6">
          <input type="password" placeholder="Código de acceso" value={code} onChange={e => {
          setCode(e.target.value);
          setError(false);
        }} onKeyDown={e => e.key === 'Enter' && handleUnlock()} className="w-full px-4 py-3 rounded-xl bg-[#141414] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#006847] focus:border-transparent text-center" data-id="element-7" />
          {error && <p className="text-[#CE1126] text-xs" data-id="element-8">Código incorrecto</p>}
          <button onClick={handleUnlock} className="w-full py-3 rounded-xl bg-[#006847] text-white font-bold text-sm hover:bg-[#005a3d] transition-colors" data-id="element-9">
            Acceder
          </button>
        </div>
      </div>
    </div>;
}
/** Routes Capricho de Canela to PublicMenuPage (premium theme), others to RestaurantPage */
function SmartRestaurantPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  // Maintenance gate: check URL param or session
  if (slug && MAINTENANCE_SLUGS.includes(slug)) {
    const urlParams = new URLSearchParams(window.location.search);
    const hasDevParam = urlParams.get('dev') === 'noiza2026';
    const hasSessionAccess = sessionStorage.getItem('noiza_dev_access') === 'true';
    if (hasDevParam && !hasSessionAccess) {
      sessionStorage.setItem('noiza_dev_access', 'true');
    }
    if (!hasDevParam && !hasSessionAccess) {
      return <MaintenancePage data-id="element-10" />;
    }
  }
  if (slug === 'capricho-de-canela') {
    return <PublicMenuPage data-id="element-11" />;
  }
  return <RestaurantPage data-id="element-12" />;
}
/** Redirects /admin/:slug → /admin/:slug/operacion (station select) */
function AdminRedirect() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  return <Navigate to={`/admin/${slug}/operacion`} replace data-id="element-13" />;
}
/**
 * Subdomain-aware admin redirect.
 * When on a tenant subdomain, /admin → /admin/dashboard
 */
function SubdomainAdminRedirect({
  slug
}: {
  slug: string;
}) {
  return <Navigate to="/admin/operacion" replace data-id="element-14" />;
}
/**
 * Wraps TenantProvider to inject the subdomain slug
 * when there's no :slug in the path params.
 */
function SubdomainTenantProvider({
  children,
  slug
}: {
  children: React.ReactNode;
  slug: string;
}) {
  // We pass the slug through a context-compatible wrapper
  // TenantProvider reads from useParams, but we need to override it.
  // Instead, we'll use the updated TenantProvider that accepts an optional slug prop.
  return <TenantProvider subdomainSlug={slug} data-id="element-15">{children}</TenantProvider>;
}
/**
 * Main routing component.
 * Detects subdomain and renders appropriate routes.
 */
function AppRoutes() {
  const subdomainSlug = getSubdomainSlug();
  // ─── SUBDOMAIN MODE ───
  // When accessed via {slug}.noiza.estrategiasdigitalesaz.com
  if (subdomainSlug) {
    return <Routes data-id="element-16">
        {/* Public restaurant page at root */}
        <Route path="/" element={<PublicMenuPage subdomainSlug={subdomainSlug} data-id="element-18" />} data-id="element-17" />

        {/* Cart & checkout still work */}
        <Route path="/cart" element={<CartPage data-id="element-20" />} data-id="element-19" />
        <Route path="/checkout" element={<CheckoutPage data-id="element-22" />} data-id="element-21" />
        <Route path="/order-confirmation" element={<OrderConfirmationPage data-id="element-24" />} data-id="element-23" />
        <Route path="/track/:trackingToken" element={<TrackingPage data-id="element-26" />} data-id="element-25" />

        {/* Table ordering */}
        <Route path="/table/:tableNumber" element={<TablePage data-id="element-28" />} data-id="element-27" />
        <Route path="/mesa/:tableNumber/:token" element={<TablePage data-id="element-30" />} data-id="element-29" />

        {/* Admin routes — no :slug needed in path, resolved from subdomain */}
        <Route path="/admin/login" element={<LoginPage subdomainSlug={subdomainSlug} data-id="element-32" />} data-id="element-31" />
        <Route path="/admin" element={<SubdomainAdminRedirect slug={subdomainSlug} data-id="element-34" />} data-id="element-33" />
        <Route path="/admin/operacion" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-36">
              <StationSelectPage data-id="element-37" />
            </SubdomainTenantProvider>} data-id="element-35" />
        <Route path="/admin/overview" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-39">
              <CajaOverviewPage data-id="element-40" />
            </SubdomainTenantProvider>} data-id="element-38" />
        <Route path="/admin/dashboard" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-42">
              <DashboardPage data-id="element-43" />
            </SubdomainTenantProvider>} data-id="element-41" />
        <Route path="/admin/caja" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-45">
              <CajaOverviewPage data-id="element-46" />
            </SubdomainTenantProvider>} data-id="element-44" />
        <Route path="/admin/mesas" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-48">
              <TableManagementPage data-id="element-49" />
            </SubdomainTenantProvider>} data-id="element-47" />
        <Route path="/admin/mesas/qr" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-51">
              <TableQRPage data-id="element-52" />
            </SubdomainTenantProvider>} data-id="element-50" />
        <Route path="/admin/kds" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-54">
              <KDSPage data-id="element-55" />
            </SubdomainTenantProvider>} data-id="element-53" />
        <Route path="/admin/menu" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-57">
              <MenuManagementPage data-id="element-58" />
            </SubdomainTenantProvider>} data-id="element-56" />
        <Route path="/admin/inventory" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-60">
              <InventoryPage data-id="element-61" />
            </SubdomainTenantProvider>} data-id="element-59" />
        <Route path="/admin/metrics" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-63">
              <MetricsPage data-id="element-64" />
            </SubdomainTenantProvider>} data-id="element-62" />

        {/* Add import route for subdomain mode */}
        <Route path="/admin/import" element={<SubdomainTenantProvider slug={subdomainSlug} data-id="element-66">
              <ImportPage data-id="element-67" />
            </SubdomainTenantProvider>} data-id="element-65" />

        {/* NOIZA Platform Metrics (global, not tenant-specific) */}
        <Route path="/admin/noiza/metrics" element={<NoizaMetricsPage data-id="element-69" />} data-id="element-68" />

        {/* Fallback: also support legacy path-based admin on subdomain */}
        <Route path="/admin/:slug/login" element={<LoginPage data-id="element-71" />} data-id="element-70" />
        <Route path="/admin/:slug" element={<AdminRedirect data-id="element-73" />} data-id="element-72" />
        <Route path="/admin/:slug/operacion" element={<TenantProvider data-id="element-75">
              <StationSelectPage data-id="element-76" />
            </TenantProvider>} data-id="element-74" />
        <Route path="/admin/:slug/dashboard" element={<TenantProvider data-id="element-78">
              <DashboardPage data-id="element-79" />
            </TenantProvider>} data-id="element-77" />
        <Route path="/admin/:slug/caja" element={<TenantProvider data-id="element-81">
              <CajaOverviewPage data-id="element-82" />
            </TenantProvider>} data-id="element-80" />
        <Route path="/admin/:slug/mesas" element={<TenantProvider data-id="element-84">
              <TableManagementPage data-id="element-85" />
            </TenantProvider>} data-id="element-83" />
        <Route path="/admin/:slug/mesas/qr" element={<TenantProvider data-id="element-87">
              <TableQRPage data-id="element-88" />
            </TenantProvider>} data-id="element-86" />
        <Route path="/admin/:slug/kds" element={<TenantProvider data-id="element-90">
              <KDSPage data-id="element-91" />
            </TenantProvider>} data-id="element-89" />
        <Route path="/admin/:slug/menu" element={<TenantProvider data-id="element-93">
              <MenuManagementPage data-id="element-94" />
            </TenantProvider>} data-id="element-92" />
        <Route path="/admin/:slug/inventory" element={<TenantProvider data-id="element-96">
              <InventoryPage data-id="element-97" />
            </TenantProvider>} data-id="element-95" />
        <Route path="/admin/:slug/metrics" element={<TenantProvider data-id="element-99">
              <MetricsPage data-id="element-100" />
            </TenantProvider>} data-id="element-98" />

        {/* Add legacy import route on subdomain */}
        <Route path="/admin/:slug/import" element={<TenantProvider data-id="element-102">
              <ImportPage data-id="element-103" />
            </TenantProvider>} data-id="element-101" />

        {/* Catch-all: any other path on subdomain → public menu */}
        <Route path="*" element={<PublicMenuPage subdomainSlug={subdomainSlug} data-id="element-105" />} data-id="element-104" />
      </Routes>;
  }
  // ─── STANDARD PATH-BASED MODE ───
  // When accessed via noiza.estrategiasdigitalesaz.com/:slug
  return <Routes data-id="element-106">
      {/* Customer Facing */}
      <Route path="/" element={<HomePage data-id="element-108" />} data-id="element-107" />
      <Route path="/restaurants" element={<RestaurantListingPage data-id="element-110" />} data-id="element-109" />
      <Route path="/r/:slug" element={<SmartRestaurantPage data-id="element-112" />} data-id="element-111" />
      <Route path="/r/:slug/menu" element={<MenuPage data-id="element-114" />} data-id="element-113" />
      <Route path="/r/:slug/table/:tableNumber" element={<TablePage data-id="element-116" />} data-id="element-115" />
      <Route path="/r/:slug/mesa/:tableNumber/:token" element={<TablePage data-id="element-118" />} data-id="element-117" />
      <Route path="/cart" element={<CartPage data-id="element-120" />} data-id="element-119" />
      <Route path="/checkout" element={<CheckoutPage data-id="element-122" />} data-id="element-121" />
      <Route path="/order-confirmation" element={<OrderConfirmationPage data-id="element-124" />} data-id="element-123" />
      <Route path="/track/:trackingToken" element={<TrackingPage data-id="element-126" />} data-id="element-125" />

      {/* NOIZA Platform Metrics (global, not tenant-specific) */}
      <Route path="/admin/noiza/metrics" element={<NoizaMetricsPage data-id="element-128" />} data-id="element-127" />

      {/* Admin - Login */}
      <Route path="/admin/:slug/login" element={<LoginPage data-id="element-130" />} data-id="element-129" />

      {/* Admin - Protected by TenantProvider */}
      <Route path="/admin/:slug" element={<AdminRedirect data-id="element-132" />} data-id="element-131" />
      <Route path="/admin/:slug/operacion" element={<TenantProvider data-id="element-134">
            <StationSelectPage data-id="element-135" />
          </TenantProvider>} data-id="element-133" />
      <Route path="/admin/:slug/dashboard" element={<TenantProvider data-id="element-137">
            <DashboardPage data-id="element-138" />
          </TenantProvider>} data-id="element-136" />
      <Route path="/admin/:slug/caja" element={<TenantProvider data-id="element-140">
            <CajaOverviewPage data-id="element-141" />
          </TenantProvider>} data-id="element-139" />
      <Route path="/admin/:slug/mesas" element={<TenantProvider data-id="element-143">
            <TableManagementPage data-id="element-144" />
          </TenantProvider>} data-id="element-142" />
      <Route path="/admin/:slug/mesas/qr" element={<TenantProvider data-id="element-146">
            <TableQRPage data-id="element-147" />
          </TenantProvider>} data-id="element-145" />
      <Route path="/admin/:slug/kds" element={<TenantProvider data-id="element-149">
            <KDSPage data-id="element-150" />
          </TenantProvider>} data-id="element-148" />
      <Route path="/admin/:slug/menu" element={<TenantProvider data-id="element-152">
            <MenuManagementPage data-id="element-153" />
          </TenantProvider>} data-id="element-151" />
      <Route path="/admin/:slug/inventory" element={<TenantProvider data-id="element-155">
            <InventoryPage data-id="element-156" />
          </TenantProvider>} data-id="element-154" />
      <Route path="/admin/:slug/metrics" element={<TenantProvider data-id="element-158">
            <MetricsPage data-id="element-159" />
          </TenantProvider>} data-id="element-157" />

      {/* Add import route for path-based mode */}
      <Route path="/admin/:slug/import" element={<TenantProvider data-id="element-161">
            <ImportPage data-id="element-162" />
          </TenantProvider>} data-id="element-160" />

      {/* Public Restaurant Page (Must be last) */}
      <Route path="/:slug" element={<SmartRestaurantPage data-id="element-164" />} data-id="element-163" />
    </Routes>;
}
export function App() {
  return <CartProvider data-id="element-165">
      <TableOrderProvider data-id="element-166">
        <KitchenPauseProvider data-id="element-167">
          <BrowserRouter data-id="element-168">
            <KitchenPausedBanner data-id="element-169" />
            <div className="min-h-screen flex flex-col bg-brand-offwhite font-sans text-gray-900" data-id="element-170">
              <AppRoutes data-id="element-171" />
            </div>
          </BrowserRouter>
        </KitchenPauseProvider>
      </TableOrderProvider>
    </CartProvider>;
}